/**
 * Event tracking constants for the Services page
 * Organized by sections: Listed Services, Consultation Rooms, Categories, Group Services
 * 
 * Note: Some events include custom_params for additional context
 */

const FEATURE_CLICKED = 'feature_clicked' as const;
const SECTION_CLICKED = 'section_clicked' as const;
const TAB = 'services' as const;

/**
 * Custom params type for events that need additional data
 */
export interface ServiceCustomParams {
  // For new_service_created
  creation_section?: 'listed_services' | 'group_services';
  
  // For settings_updated
  is_pinned_updated?: boolean;
  custom_service_name_set?: boolean;
  custom_description_set?: boolean;
  categories_selected?: boolean;
  
  // For content_updated
  short_description_updated?: boolean;
  long_description_entered?: boolean;
  save_type?: 'save' | 'save_and_publish';
  
  // For payment_updated
  charge_online_enabled?: boolean;
  price_entered?: boolean;
}

/**
 * All services page events
 */
export const SERVICES_EVENTS = {
  // Top-level service creation (can happen in either section)
  NEW_SERVICE_CREATED: {
    action: 'new_service_created',
    type: FEATURE_CLICKED,
    tab: TAB,
  },

  // Listed Services Section (Branch-level)
  LISTED_SERVICES: {
    SECTION_CLICKED: {
      action: 'section_clicked',
      type: SECTION_CLICKED,
      tab: TAB,
      section: 'listed_services',
    },
    SERVICE_CLICKED: {
      action: 'service_clicked',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
    },
    PF_SERVICE_ADDED: {
      action: 'pf_service_added',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
    },
    PF_INFO_REQUESTED: {
      action: 'pf_info_requested',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
    },
    SETTINGS_UPDATED: {
      action: 'settings_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
      // Include custom_params in the tracking call
    },
    CONTENT_UPDATED: {
      action: 'content_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
      // Include custom_params in the tracking call
    },
    PAYMENT_UPDATED: {
      action: 'payment_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
      // Include custom_params in the tracking call
    },
    SCHEDULE_UPDATED: {
      action: 'schedule_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
    },
    SEARCHED: {
      action: 'searched',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'listed_services',
    },
  },

  // Consultation Rooms Section
  CONSULTATION_ROOMS: {
    SECTION_CLICKED: {
      action: 'section_clicked',
      type: SECTION_CLICKED,
      tab: TAB,
      section: 'consultation_rooms',
    },
    RENAMED: {
      action: 'renamed',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'consultation_rooms',
    },
    NEW_ROOM_ADDED: {
      action: 'new_room_added',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'consultation_rooms',
    },
  },

  // Categories Section
  CATEGORIES: {
    SECTION_CLICKED: {
      action: 'section_clicked',
      type: SECTION_CLICKED,
      tab: TAB,
      section: 'categories',
    },
    NEW_CATEGORY_CREATED: {
      action: 'new_category_created',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'categories',
    },
    SPOTLIGHT_CLICKED: {
      action: 'spotlight_clicked',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'categories',
    },
  },

  // Group Services Section (Org-level)
  GROUP_SERVICES: {
    SECTION_CLICKED: {
      action: 'section_clicked',
      type: SECTION_CLICKED,
      tab: TAB,
      section: 'group_services',
    },
    SERVICE_CLICKED: {
      action: 'service_clicked',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'group_services',
    },
    SETTINGS_UPDATED: {
      action: 'settings_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'group_services',
      // Include custom_params in the tracking call
    },
    CONTENT_UPDATED: {
      action: 'content_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'group_services',
      // Include custom_params in the tracking call
    },
    PAYMENT_UPDATED: {
      action: 'payment_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'group_services',
      // Include custom_params in the tracking call
    },
    PHARMACIES_SETTING_UPDATED: {
      action: 'pharmacies_setting_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'group_services',
    },
    PHARMACIES_SETTING_BULK_UPDATED: {
      action: 'pharmacies_setting_bulk_updated',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'group_services',
    },
    SEARCHED: {
      action: 'searched',
      type: FEATURE_CLICKED,
      tab: TAB,
      section: 'group_services',
    },
  },
  BLOCK_OUT_DAYS: {
    SECTION_CLICKED: {
      action: 'section_clicked',
      type: SECTION_CLICKED,
      tab: TAB,
      section: 'block_out_days',
    },
  },
  BLOCK_OUT_DAY_ADDED: {
    action: 'block_out_day_added',
    type: FEATURE_CLICKED,
    tab: TAB,
    section: 'block_out_days',
  },
} as const;

